import { Error } from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import { TabsWithMountingPanels } from "@pimo/pimo-components";
import { Attachment, IMPACT_SERIES_KEYS, UnsavedAttachment } from "aint-types";
import { FC } from "react";
import { FieldErrors, FieldValues, useFormContext } from "react-hook-form";
import { Form } from "react-router-dom";

import { EditInitiativeBaseTab } from "./edit-initiative-base-tab";
import { EditInitiativeDialogFireEventType } from "./edit-initiative-dialog";
import { EditInitiativeImpactTab } from "./edit-initiative-impact-tab";

export interface EditInitiativeDialogContentProps {
  /** submit handler */
  onSubmit: (initiative: FieldValues) => void;
  /** invalid handler */
  onInvalid: (errors: FieldErrors) => void;
  /** Should the inter oe region field be shown */
  showInterOeRegionField: boolean;
  /** Should the business unit field be shown */
  showBusinessUnitField: boolean;
  /** Should the category field be shown */
  showCategoryField: boolean;
  /** Should the OE Program fields be shown */
  showOeProgramFields: boolean;
  /** Should the Elevate option be shown for the implementation program field */
  showElevateImplProgramOption: boolean;
  /** Should the line of business options for the AZ Partners OE be shown */
  showExtendedLobOptions: boolean;
  /** Name of the form */
  formName: string;
  /** Is this creating an initiative */
  isCreate: boolean;
  usersWithAccessToOE: string[];
  activeInitiativeUID: string;
  fireEvent?: EditInitiativeDialogFireEventType;
  supportingDocuments?: (UnsavedAttachment | Attachment)[];
}

/** contents of the edit dialog,  */
export const EditInitiativeDialogContent: FC<
  EditInitiativeDialogContentProps
> = ({
  formName,
  onSubmit,
  onInvalid,
  showInterOeRegionField,
  showBusinessUnitField,
  showCategoryField,
  showOeProgramFields,
  showElevateImplProgramOption,
  showExtendedLobOptions,
  usersWithAccessToOE,
  isCreate,
  activeInitiativeUID,
  fireEvent,
  supportingDocuments,
}: EditInitiativeDialogContentProps) => {
  const theme = useTheme();

  const {
    handleSubmit,
    formState: { errors },
  } = useFormContext();

  const tabsWithErrors = getTabsWithErrors(errors);

  return (
    <Box
      sx={{
        padding: "16px 24px",
        margin: theme.spacing(5),
        marginTop: 0,
      }}
    >
      <Form id={formName} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <TabsWithMountingPanels
          tabContainerSx={{
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 9999,
            borderBottom: "1px solid rgba(0, 0, 0, 0.33)",
          }}
          panels={[
            {
              title: "Initiative",
              icon: tabsWithErrors.baseTab ? (
                <Error color="error" />
              ) : undefined,
              content: (
                <EditInitiativeBaseTab
                  showInterOeRegionField={showInterOeRegionField}
                  showBusinessUnitField={showBusinessUnitField}
                  showCategoryField={showCategoryField}
                  showOeProgramFields={showOeProgramFields}
                  showExtendedLobOptions={showExtendedLobOptions}
                  showElevateImplProgramOption={showElevateImplProgramOption}
                  usersWithAccessToOE={usersWithAccessToOE}
                  isCreate={isCreate}
                  fireEvent={fireEvent}
                  supportingDocuments={supportingDocuments}
                />
              ),
            },
            {
              title: "Impact Plan",
              icon: tabsWithErrors.impactTab ? (
                <Error color="error" />
              ) : undefined,
              content: (
                <EditInitiativeImpactTab
                  isOnCreate={isCreate}
                  activeInitiativeUID={activeInitiativeUID}
                />
              ),
            },
          ]}
          renderHidden={true}
        />
      </Form>
    </Box>
  );
};

const IMPACT_TAB_FIELDS = [
  "startDate",
  "endDate",
  "monitoringEndDate",
  "primaryImpactDriver",
  "baselineYear",
  "signOffCoo",
  "signOffCfo",
  "signOffChro",
  ...IMPACT_SERIES_KEYS,
];

function getTabsWithErrors(errors: FieldErrors<FieldValues>): {
  baseTab: boolean;
  impactTab: boolean;
} {
  const tabsWithErrors = { baseTab: false, impactTab: false };

  for (const fieldName of Object.keys(errors)) {
    if (IMPACT_TAB_FIELDS.includes(fieldName)) {
      tabsWithErrors.impactTab = true;
    } else {
      tabsWithErrors.baseTab = true;
    }
  }

  return tabsWithErrors;
}
